/**
 * AI Art Director — Creative Review schema (data-driven contract).
 *
 * This is CREATIVE review, not quality/JSON/editor/compiler/layout validation.
 * The vocabulary here — review areas, detections, score categories, severities —
 * is data, so the rule set iterates it and adding a critique is a data change.
 */

export const REVIEW_SCHEMA_ID = 'creativereview/1';
export const REVIEW_VERSION = 1;

/** Everything the Art Director reviews. Every area must be covered by a rule. */
export const REVIEW_AREAS = Object.freeze([
  'Business Intent',
  'Marketing Intent',
  'Communication Strategy',
  'Visual Narrative',
  'Visual Language',
  'Style Genome',
  'Composition Strategy',
  'Hierarchy Strategy',
  'Typography Strategy',
  'Color Strategy',
  'Photography Strategy',
  'Illustration Strategy',
  'Shape Strategy',
  'Decoration Strategy',
  'CTA Strategy',
  'Premium Cues',
  'Pattern Selection',
  'Memory Constraints',
  'Diversity',
  'Marketplace Readiness',
]);

/** Everything the Art Director must be able to detect. */
export const DETECTIONS = Object.freeze([
  'Weak hierarchy',
  'Weak marketing message',
  'Weak CTA',
  'Weak storytelling',
  'Weak premium feeling',
  'Weak differentiation',
  'Weak emotional direction',
  'Weak visual language',
  'Wrong genome',
  'Wrong pattern selection',
  'Too much complexity',
  'Too much simplicity',
  'Weak originality',
  'Weak marketplace value',
]);

/** The scores the review reports. */
export const SCORE_CATEGORIES = Object.freeze([
  'creative', 'marketplace', 'premium', 'originality', 'marketing', 'communication', 'storytelling',
]);

export const SEVERITY = Object.freeze({ BLOCKER: 'blocker', MAJOR: 'major', MINOR: 'minor' });

/** How many points a finding removes from each score it touches. */
export const SEVERITY_WEIGHT = Object.freeze({ blocker: 40, major: 20, minor: 10 });

/** Severity order for ranking revision requests (most urgent first). */
export const SEVERITY_ORDER = Object.freeze(['blocker', 'major', 'minor']);

/** A plan ships only when it clears these and carries no blocker. */
export const DEFAULT_THRESHOLDS = Object.freeze({ creative: 70, marketplace: 70 });

/**
 * The bar for a review that saw the rendered page.
 *
 * Structural review answers "is this a legal, buildable document?" — 70 is the
 * right bar for that. A visual review answers a different question: "is this
 * professionally art-directed?" A flyer with one flat type rank scores 80 and
 * would pass the structural bar, and a browser QA rejected exactly such pages
 * as wireframes. Judged visually, one major visual weakness is enough to send
 * a design back.
 */
export const VISUAL_THRESHOLDS = Object.freeze({ creative: 85, marketplace: 80 });

/**
 * Plan decision → the genome dimension it must stay faithful to.
 * A plan that drifts from its own declared language has no visual language.
 */
export const LANGUAGE_COHERENCE = Object.freeze({
  compositionStrategy: 'composition',
  typographyStrategy: 'typography',
  colorStrategy: 'color',
});

/** The supporting media decisions, checked against the same genome. */
export const MEDIUM_COHERENCE = Object.freeze({
  photographyStrategy: 'photography',
  illustrationStrategy: 'illustration',
  shapeStrategy: 'shape',
  decorationStrategy: 'decoration',
});

/** Composition sizing — how many patterns read as considered rather than thin or noisy. */
export const COMPOSITION_LIMITS = Object.freeze({
  minPatterns: 2, maxPatterns: 6, minPriorities: 2, maxPriorities: 7, minPremiumCues: 3,
});

/** Similarity at or above this reads as derivative even when memory allows it. */
export const DERIVATIVE_SIMILARITY = 0.5;

/** CTA treatments too quiet to carry an offer-led design. */
export const QUIET_CTA = Object.freeze(['text-link', 'none']);
